import React, { useCallback, useMemo, useRef } from 'react'
import { Building2, Compass, GitBranchPlus, Plus, Sparkles } from 'lucide-react'
import { type NodeProps, Position } from '@xyflow/react'
import { DismissibleTooltip } from '../../common/DismissibleTooltip'
import { BaseNode } from './Base/BaseNode'
import type { DockAction } from './Base/BaseNodeTypes'
import { useStableEdgeTopology } from '../hooks/useStableEdgeTopology'
import { useNodeActions } from '../../../context/nodeActions'
import { CATEGORY_STYLES } from '../../../constants/mindmapConfig'
import type { NeedFlowNode } from '../../../types/mindmap-components'
import { NodeQualityChip, useNodeQualityScore } from '../../../features/nodeDataQuality'
import { ConfidenceBadge, useConfidenceScore } from '../../../features/confidenceBadge'
import {
  LeftHandleWrapper,
  HandleLine,
  HandleButton,
  HandleButtonIcon,
  LeftSourceHandle,
} from './styles/HandleStyles'

const UI_TEXT = {
  categoryLabel: 'Unmet need',
  categoryDescription:
    "Describe a moment that doesn't work well today and what the customer actually wants when it happens. Plain text — keep it specific to the situation, not the tool.",
  leftHandleTooltip: 'What venture is this for?',
}

/**
 * Need node — a plain-text "unmet need" statement attached under a venture.
 * Mirrors IdeaNode in shape: BaseNode renders the editable label.
 */
export const NeedNode = React.memo((props: NodeProps<NeedFlowNode>) => {
  const { data, id } = props
  const nodeData = data
  const qualityResult = useNodeQualityScore(id)
  const {
    score: confidenceScore,
    isPending: isConfidencePending,
    scoreEntry: confidenceEntry,
  } = useConfidenceScore(id, nodeData.mindmapId)

  const { handleAddParent, handleDirectChildCreation } = useNodeActions()
  const chatApiRef = useRef<{ autoGenerate: () => void } | null>(null)

  const handleChatReady = useCallback((api: { autoGenerate: () => void }) => {
    chatApiRef.current = api
  }, [])

  const renderHeaderActions = useCallback(
    () => (
      <>
        <ConfidenceBadge
          score={confidenceScore}
          isPending={isConfidencePending}
          nodeId={id}
          nodeLabel={nodeData.label}
          mindmapId={nodeData.mindmapId}
          scoreEntry={confidenceEntry}
        />
        {qualityResult ? (
          <NodeQualityChip qualityResult={qualityResult} nodeCategory="need" inline />
        ) : null}
      </>
    ),
    [
      qualityResult,
      confidenceScore,
      isConfidencePending,
      confidenceEntry,
      id,
      nodeData.label,
      nodeData.mindmapId,
    ]
  )

  const dockActions: DockAction[] = useMemo(
    () => [
      {
        id: 'add-idea-group',
        icon: GitBranchPlus,
        label: 'Explore Solutions',
        group: 'branch' as const,
        onClick: () => {},
        dropdownChildren: [
          {
            id: 'add-own',
            icon: Plus,
            label: 'Add your own',
            subtitle: 'Type it yourself',
            group: 'branch' as const,
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation()
              handleDirectChildCreation(id, 'idea')
            },
          },
          {
            id: 'ai-suggest',
            icon: Sparkles,
            label: 'AI suggest',
            subtitle: 'Generate options',
            group: 'branch' as const,
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation()
              chatApiRef.current?.autoGenerate()
            },
          },
        ],
      },
    ],
    [id, handleDirectChildCreation]
  )

  const leftHandleWrapperRef = useRef<HTMLDivElement>(null)
  const ventureCategoryStyle = CATEGORY_STYLES.venture

  const edgeTopology = useStableEdgeTopology()
  const hasParent = useMemo(
    () => edgeTopology.some((edge) => edge.target === id),
    [edgeTopology, id]
  )

  const renderLeftHandle = useCallback(
    () => (
      <DismissibleTooltip title={UI_TEXT.leftHandleTooltip} mouseEnterDelay={0.5} placement="left">
        <LeftHandleWrapper
          ref={leftHandleWrapperRef}
          className="nodrag"
          onClick={(e) => {
            e.stopPropagation()
            handleAddParent(id, 'need', leftHandleWrapperRef.current ?? undefined)
          }}
        >
          <HandleButton $accentColor={ventureCategoryStyle.iconColor}>
            <HandleButtonIcon>
              <Building2 size={14} color={ventureCategoryStyle.iconColor} strokeWidth={2.5} />
            </HandleButtonIcon>
            <LeftSourceHandle type="source" position={Position.Left} id="left-source" />
          </HandleButton>
          <HandleLine $accentColor={ventureCategoryStyle.iconColor} $direction="left" />
        </LeftHandleWrapper>
      </DismissibleTooltip>
    ),
    [id, handleAddParent, ventureCategoryStyle.iconColor]
  )

  return (
    <BaseNode
      id={props.id}
      nodeData={nodeData}
      icon={Compass}
      categoryLabel={UI_TEXT.categoryLabel}
      categoryDescription={UI_TEXT.categoryDescription}
      dockActions={dockActions}
      renderHeaderActions={renderHeaderActions}
      onChatReady={handleChatReady}
      renderLeftHandle={hasParent ? undefined : renderLeftHandle}
    />
  )
})

NeedNode.displayName = 'NeedNode'
